import { USER_RHYTHM_SESSION_DAYS, type AdminUserRhythm } from '@steado/admin-contract/rhythm';
import { useQuery } from '@tanstack/react-query';

import { OwnerBadge } from '@/components/panel/OwnerBadge';
import { Empty, FieldRow, FieldRows, Panel, TabSkeleton } from '@/components/panel/panels';
import { BadgeCell } from '@/components/table/cells';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { formatDate, formatLocalDate } from '@/lib/format';
import { STATE_TONES, count, localMinute } from '@/screens/rhythm/rhythm-format';
import { userRhythmQueryOptions } from '@/screens/rhythm/rhythm-query';

import { TabError } from './TabError';

/**
 * Where Rhythm has this user: which side of the holdout, which lifecycle
 * state, how the state moved, and when and how they open the app.
 *
 * Read only, and reading it never places the user: only the sweep creates a
 * profile. Times of use and nothing about what was logged.
 */
export function RhythmTab({ userId }: { readonly userId: string }) {
  const { api } = useAuth();
  const query = useQuery(userRhythmQueryOptions(api, userId));

  if (query.isPending) return <TabSkeleton rows={3} />;
  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
      <PlacementPanel rhythm={query.data} />
      <HistoryPanel rhythm={query.data} />
      <UpcomingPanel rhythm={query.data} />
      <LedgerPanel rhythm={query.data} />
      <SessionsPanel rhythm={query.data} />
    </div>
  );
}

function PlacementPanel({ rhythm }: { readonly rhythm: AdminUserRhythm }) {
  const { profile, bucket, enabled } = rhythm;

  return (
    <Panel title="Arm and state" owner={<OwnerBadge>Derived, not set</OwnerBadge>} bleed>
      <FieldRows>
        {profile === null ? (
          <FieldRow
            label="Placed"
            value={
              enabled
                ? 'Not yet. They get an arm and a state when their day next ends.'
                : 'No. Rhythm is switched off.'
            }
            tone="muted"
          />
        ) : (
          <>
            <FieldRow
              label="Arm"
              value={<BadgeCell label={profile.arm} tone={profile.arm === 'holdout' ? 'mute' : 'info'} />}
            />
            <FieldRow
              label="State"
              value={
                <span className="flex flex-wrap items-center gap-2">
                  <BadgeCell label={profile.state} tone={STATE_TONES[profile.state]} dot />
                  <span className="font-normal text-muted-foreground">
                    since {formatDate(profile.stateSince)}
                  </span>
                </span>
              }
            />
            <FieldRow
              label="Last opened or logged"
              value={profile.lastActiveDate === null ? null : formatLocalDate(profile.lastActiveDate)}
            />
            {profile.lapseStartDate === null ? null : (
              <FieldRow label="Away since" value={formatLocalDate(profile.lapseStartDate)} tone="warn" />
            )}
            <FieldRow
              label="Days closed through"
              value={profile.closedThrough === null ? null : formatLocalDate(profile.closedThrough)}
            />
            {TIMING_ROWS.map((row) => {
              const minute = profile.timing[row.slot];
              return (
                <FieldRow
                  key={row.slot}
                  label={row.label}
                  value={minute === null ? `default, ${row.fallback}` : localMinute(minute)}
                  mono={minute !== null}
                  {...(minute === null ? { tone: 'muted' as const } : {})}
                />
              );
            })}
            <FieldRow
              label="Times worked out"
              value={profile.timing.computedOn === null ? null : formatLocalDate(profile.timing.computedOn)}
            />
          </>
        )}
        <FieldRow label="Holdout bucket" value={`${String(bucket)} of 0 to 99`} mono />
      </FieldRows>
    </Panel>
  );
}

/** Newest first. A user's first placement has no state it came from. */
function HistoryPanel({ rhythm }: { readonly rhythm: AdminUserRhythm }) {
  return (
    <Panel title="State changes" bleed>
      {rhythm.transitions.length === 0 ? (
        <div className="border-t border-border">
          <Empty title="No state changes" body="Rhythm has not placed this account yet." />
        </div>
      ) : (
        <ul>
          {rhythm.transitions.map((row) => (
            <li
              key={`${row.occurredAt}:${row.to}`}
              className="flex items-center gap-1.5 border-t border-border px-[18px] py-2"
            >
              {row.from === null ? (
                <span className="text-[13px] text-muted-foreground">first placed</span>
              ) : (
                <BadgeCell label={row.from} tone={STATE_TONES[row.from]} />
              )}
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
              <span className="sr-only">to</span>
              <BadgeCell label={row.to} tone={STATE_TONES[row.to]} />
              <span className="ml-auto font-mono text-[12.5px] text-muted-foreground">
                {formatLocalDate(row.localDate)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** The user's own send time per slot, and what they get without one. */
const TIMING_ROWS = [
  { slot: 'evening', label: 'Evening nudge at', fallback: '20:00' },
  { slot: 'morning', label: 'Rest-day tip at', fallback: '10:30' },
  { slot: 'sunday', label: 'Sunday summary at', fallback: '19:15' },
  { slot: 'any', label: 'Win-back at', fallback: '18:00' },
] as const;

const EVENT_LABELS: Readonly<Record<string, string>> = {
  'workout.reminder': 'Workout reminder',
  'meal.reminder': 'Meal reminder',
  'streak.at_risk': 'Evening streak nudge',
  'rhythm.day_open': 'Day still open',
  'rhythm.winback': 'Win-back',
  'coaching.tip': 'Rest-day tip',
  'weekly.summary': 'Weekly summary',
  'monthly.recap': 'Monthly Recap',
};

/**
 * What is scheduled for them from now, at the local time each is set for.
 * The nudge, the tip and the summary show their own time once timing is on
 * and they are in the Rhythm arm; everything else keeps its fixed time.
 */
function UpcomingPanel({ rhythm }: { readonly rhythm: AdminUserRhythm }) {
  return (
    <Panel title="Upcoming pushes" bleed>
      {rhythm.upcoming.length === 0 ? (
        <div className="border-t border-border">
          <Empty title="Nothing scheduled" body="No reminder, nudge, tip or summary is set for them." />
        </div>
      ) : (
        <ul>
          {rhythm.upcoming.map((row) => (
            <li
              key={`${row.eventType}:${row.localDate}:${row.localTime}`}
              className="flex items-baseline gap-3 border-t border-border px-[18px] py-2"
            >
              <span className="text-[13px] text-foreground">
                {EVENT_LABELS[row.eventType] ?? row.eventType}
              </span>
              <span className="ml-auto font-mono text-[12.5px] text-muted-foreground">
                {formatLocalDate(row.localDate)} {row.localTime}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const DAY_TONES: Readonly<Record<AdminUserRhythm['ledger'][number]['kind'], string>> = {
  kept: 'bg-ok-fg',
  rest: 'bg-info-fg/40',
  passed: 'bg-warn-fg/60',
  missed: 'bg-err-fg',
};

const DAY_LABELS: Readonly<Record<AdminUserRhythm['ledger'][number]['kind'], string>> = {
  kept: 'kept',
  rest: 'planned rest',
  passed: 'covered by the pass',
  missed: 'missed',
};

/**
 * The streak's ledger, a square per closed day, oldest first: kept, a planned
 * rest day, covered by the week's pass, or missed. The chain as the last day
 * closed is beside it.
 */
function LedgerPanel({ rhythm }: { readonly rhythm: AdminUserRhythm }) {
  const last = rhythm.ledger.at(-1);
  return (
    <Panel
      title="Streak ledger"
      bleed
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          A day is kept with a meal, a workout or a weigh-in. A planned rest day and one missed day a week
          (the pass) neither count nor break the streak. A gap is a stretch away: the ledger stops after
          thirty days with nothing kept.
        </p>
      }
    >
      {last === undefined ? (
        <div className="border-t border-border">
          <Empty title="No days yet" body="The ledger starts with their first kept day." />
        </div>
      ) : (
        <div className="flex flex-col gap-3 border-t border-border px-[18px] py-3.5">
          <ol className="flex flex-wrap gap-[3px]" aria-label="Closed days, oldest first">
            {rhythm.ledger.map((day) => (
              <li
                key={day.date}
                title={`${formatLocalDate(day.date)}: ${DAY_LABELS[day.kind]}, ${count(day.chainDays)} in a row`}
                className={cn('size-4 rounded-[3px]', DAY_TONES[day.kind])}
              >
                <span className="sr-only">
                  {formatLocalDate(day.date)}: {DAY_LABELS[day.kind]}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-[13px] text-muted-foreground">
            As {formatLocalDate(last.date)} closed:{' '}
            <span className="font-mono text-foreground">{count(last.chainDays)}</span> days,{' '}
            <span className="font-mono text-foreground">{count(last.chainWeeks)}</span> weeks in a row.
          </p>
        </div>
      )}
    </Panel>
  );
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

/**
 * When the app was opened, by the user's own weekday and hour, and how. A
 * darker cell is more sessions in that hour across the window; the busiest
 * hour is the darkest, so read the shape, not the shade.
 */
function SessionsPanel({ rhythm }: { readonly rhythm: AdminUserRhythm }) {
  const { total, hourOfWeek, entries, lastStartedAt } = rhythm.sessions;
  const busiest = Math.max(0, ...hourOfWeek.flat());

  return (
    <Panel
      title="When they open the app"
      owner={<OwnerBadge>{`Last ${String(USER_RHYTHM_SESSION_DAYS)} days`}</OwnerBadge>}
      className="xl:col-span-2"
      bleed
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          One session is a cold start, a return after ten minutes away, or a tap on a notification or widget.
          Hours are the user&apos;s own, in the timezone they were in. Recorded by app builds with the session
          recorder only.
        </p>
      }
    >
      {total === 0 ? (
        <div className="border-t border-border">
          <Empty
            title="No sessions"
            body={
              lastStartedAt === null
                ? 'Nothing recorded. Their app build may predate the recorder, or recording is switched off.'
                : `None in the last ${String(USER_RHYTHM_SESSION_DAYS)} days. The last was ${formatDate(lastStartedAt)}.`
            }
          />
        </div>
      ) : (
        // Side by side once there is room, so the counts sit beside the grid
        // rather than stretching across the panel under it.
        <div className="grid items-start border-t border-border xl:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
          <div className="overflow-x-auto px-[18px] py-3.5">
            <table className="border-separate border-spacing-[3px]" aria-label="Sessions by weekday and hour">
              <thead>
                <tr>
                  <th scope="col" className="w-9" />
                  {HOURS.map((hour) => (
                    <th
                      key={hour}
                      scope="col"
                      className="w-4 text-left font-mono text-[10.5px] font-normal text-muted-foreground"
                    >
                      <span className="sr-only">{hourLabel(hour)}</span>
                      <span aria-hidden="true">{hour % 6 === 0 ? String(hour) : ''}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hourOfWeek.map((hours, day) => (
                  <tr key={WEEKDAYS[day]}>
                    <th
                      scope="row"
                      className="pr-1.5 text-left text-[12px] font-normal text-muted-foreground"
                    >
                      {WEEKDAYS[day]}
                    </th>
                    {hours.map((sessions, hour) => (
                      <td
                        key={hour}
                        title={`${WEEKDAYS[day] ?? ''} ${hourLabel(hour)}: ${count(sessions)}`}
                        className={cn('size-4 rounded-[3px]', sessions === 0 ? 'bg-secondary' : 'bg-info-fg')}
                        style={sessions === 0 ? undefined : { opacity: 0.25 + 0.75 * (sessions / busiest) }}
                      >
                        <span className="sr-only">{count(sessions)}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-border xl:border-l xl:[&_dl>div:first-child]:border-t-0">
            <FieldRows>
              <FieldRow label="Sessions" value={count(total)} mono />
              {entries.map(({ entry, count: sessions }) => (
                <FieldRow
                  key={entry}
                  label={ENTRY_LABELS[entry]}
                  value={count(sessions)}
                  mono
                  {...(sessions === 0 ? { tone: 'muted' as const } : {})}
                />
              ))}
              <FieldRow
                label="Last session"
                value={lastStartedAt === null ? null : formatDate(lastStartedAt)}
              />
            </FieldRows>
          </div>
        </div>
      )}
    </Panel>
  );
}

const ENTRY_LABELS: Readonly<Record<AdminUserRhythm['sessions']['entries'][number]['entry'], string>> = {
  icon: 'From the icon',
  push: 'From a notification',
  widget: 'From a widget',
  shortcut: 'From a shortcut',
  link: 'From a link',
  unknown: 'Other',
};
